import { useState, useMemo } from "react";
import PropTypes from "prop-types";

import { alignItems, strCode, campaignStatus, colors } from "../../../../../../consts";
import { t } from "../../../../../../services";
import { dateUtil } from "../../../../../../utils";

import {
  Caption,
  FlexContainers,
  Paragraph2,
  SVG,
  TextButton,
  WrapperContainer
} from "../../../../../../components";
import { InfoTemplate } from "../InfoTemplate/InfoTemplate";
import { DeviceImageContainer } from "../../../FWUCampaignTable/styled";
import { DeviceModelName, DeviceTypeInfo } from "./styled";

import mercuryCharger from "../../../../../../assets/images/deviceImages/mercury_charger.png";
import { DeactivateCampaignModal } from "../../../../components/DeactivateCampaignModal/DeactivateCampaignModal";

export const CampaignInfo = ({ fwuCampaignData = {}, loading, onDeactivate = () => {} }) => {
  const [deactivateModalActive, setDeactivateModalActive] = useState(false);

  const startDateTs = useMemo(() => {
    return dateUtil.getUTCDate(new Date(fwuCampaignData?.startDate))?.getTime();
  }, [fwuCampaignData]);

  const pushNotificationDateTs = useMemo(() => {
    if (fwuCampaignData?.pushNotificationStartDate) {
      return dateUtil.getUTCDate(new Date(fwuCampaignData.pushNotificationStartDate)).getTime();
    }

    return null;
  }, [fwuCampaignData]);

  const isFWUCampaignActive = useMemo(() => {
    return fwuCampaignData?.status === campaignStatus.active;
  }, [fwuCampaignData]);

  const createdDate = useMemo(() => {
    if (fwuCampaignData?.createdDate) {
      return dateUtil.formatDateFromDayFirstFormat(fwuCampaignData.createdDate).getTime();
    }

    return "";
  }, [fwuCampaignData]);

  const onDeactivateCampaign = () => {
    setDeactivateModalActive(false);
    onDeactivate();
  };

  return (
    <>
      <WrapperContainer
        Container={FlexContainers.FlexColumnBetween}
        alignItems={alignItems.start}
        width={"100%"}
        height={"100%"}>
        <WrapperContainer Container={FlexContainers.FlexColumn} gap={16} width={"100%"}>
          <InfoTemplate title={strCode.DEVICE_INFO_TITLE}>
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
              <DeviceImageContainer Container={FlexContainers.FlexRowCenter}>
                <img src={mercuryCharger} alt="device" />
              </DeviceImageContainer>

              <WrapperContainer
                Container={FlexContainers.FlexColumnStart}
                alignItems={alignItems.start}>
                <DeviceModelName>{fwuCampaignData?.deviceModel}</DeviceModelName>
                <DeviceTypeInfo>{t(strCode.CHARGER_LABEL)}</DeviceTypeInfo>
              </WrapperContainer>
            </WrapperContainer>
          </InfoTemplate>

          <InfoTemplate title={strCode.START_DATE_TITLE}>
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
              <div>
                <SVG.CalendarIconNew />
              </div>
              <Paragraph2>{dateUtil.formatDateFromTimestamp(startDateTs)}</Paragraph2>
            </WrapperContainer>
          </InfoTemplate>

          {pushNotificationDateTs ? (
            <InfoTemplate title={strCode.PUSH_NOTIFICATION_INFO}>
              <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
                <div>
                  <SVG.CalendarIconNew />
                </div>
                <Paragraph2>{dateUtil.formatDateFromTimestamp(pushNotificationDateTs)}</Paragraph2>
              </WrapperContainer>
            </InfoTemplate>
          ) : null}

          <InfoTemplate title={strCode.NEW_FIRMWARE_VERSION_TITLE}>
            <Paragraph2>{fwuCampaignData?.versionTo}</Paragraph2>
          </InfoTemplate>

          {fwuCampaignData?.createdBy && (
            <WrapperContainer
              Container={FlexContainers.FlexColumnStart}
              width="100%"
              alignItems="start">
              <Caption>{t(strCode.OWNER_TITLE).toUpperCase()}</Caption>

              <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
                <Paragraph2>{`${fwuCampaignData.createdBy.first_name} ${fwuCampaignData.createdBy.last_name}`}</Paragraph2>
              </WrapperContainer>
            </WrapperContainer>
          )}

          {fwuCampaignData?.createdDate && (
            <WrapperContainer
              Container={FlexContainers.FlexColumnStart}
              width="100%"
              alignItems="start">
              <Caption>{t(strCode.CREATED_DATE_TITLE).toUpperCase()}</Caption>

              <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
                <SVG.CalendarIconNew />
                <Paragraph2>{dateUtil.formatDateFromTimestamp(createdDate)}</Paragraph2>
              </WrapperContainer>
            </WrapperContainer>
          )}
        </WrapperContainer>

        {isFWUCampaignActive && (
          <WrapperContainer pb={8}>
            <TextButton
              color={colors.ERROR}
              onClick={() => setDeactivateModalActive(true)}
              disabled={loading}>
              {t(strCode.DEACTIVATE_BTN)}
            </TextButton>
          </WrapperContainer>
        )}
      </WrapperContainer>

      <DeactivateCampaignModal
        campaignName={fwuCampaignData?.campaignName}
        active={deactivateModalActive}
        setActive={setDeactivateModalActive}
        onDeactivate={onDeactivateCampaign}
      />
    </>
  );
};

CampaignInfo.propTypes = {
  fwuCampaignData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    deviceModel: PropTypes.string.isRequired,
    currentFirmwares: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    device: PropTypes.string.isRequired,
    versionTo: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    deactivationDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    numberOfSucceededThings: PropTypes.number.isRequired,
    numberOfFailedThings: PropTypes.number.isRequired,
    pushNotificationStartDate: PropTypes.string,
    createdBy: PropTypes.shape({
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired
    }),
    createdDate: PropTypes.string
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onDeactivate: PropTypes.func.isRequired
};
